/**
 * Print the insurance estimate.
 *
 * The same shape as `printOrder`: render into a blank window and let the
 * browser's own "Save as PDF" make the file. No PDF library, and what is on
 * screen is what comes out. The markup itself is built in `packages/core`,
 * where a test can assert on what leaves the building.
 *
 * The masthead is assembled here because it is the only place that knows both
 * halves: `company_settings` for the company, and the profile it points at for
 * the one person an adjuster rings. Neither is a literal — see ADR 0055.
 */

import {
  renderInsuranceEstimateHtml,
  type InsuranceEstimateBranding,
  type InsuranceEstimateHeading,
  type InsuranceEstimateTotals,
  COMPANY_NAME,
} from "@roof-hawk/core";

import { COMPANY_LOGO_URL } from "../logo";
import type { CompanySettingsRow } from "./queries";

/** The person on the claim sheet, read live from their profile. */
export interface ClaimContact {
  full_name: string | null;
  phone: string | null;
  email: string | null;
}

/**
 * Build the masthead.
 *
 * Falls back deliberately rather than printing a gap: the company name falls
 * back to `COMPANY_NAME` in core, and a missing claim contact falls back to the
 * company's own number and mailbox. A claim sheet an adjuster cannot reply to
 * is worse than one addressed generally.
 *
 * The licence number is not read here and there is no field for it. The licence
 * on file names a different company; the contracts leave it off for the same
 * reason.
 */
export function insuranceBranding(
  settings: CompanySettingsRow | null,
  contact: ClaimContact | null,
  origin: string,
): InsuranceEstimateBranding {
  return {
    companyName: settings?.name?.trim() || COMPANY_NAME,
    companyAddress: settings?.address ?? null,
    logoUrl: new URL(COMPANY_LOGO_URL, origin).href,
    contactName: contact?.full_name?.trim() || settings?.name?.trim() || "",
    contactPhone: contact?.phone ?? settings?.phone ?? null,
    contactEmail: contact?.email ?? settings?.email ?? null,
  };
}

export function printInsuranceEstimate(
  totals: InsuranceEstimateTotals,
  branding: InsuranceEstimateBranding,
  heading: InsuranceEstimateHeading,
): void {
  const win = window.open("", "_blank", "width=1000,height=1100");
  if (!win) {
    window.alert(
      "Your browser blocked the print window. Allow pop-ups for this site.",
    );
    return;
  }

  win.document.write(renderInsuranceEstimateHtml(totals, branding, heading));
  win.document.close();

  // Wait for the mark before printing — the same race printOrder hit, and this
  // is a document that goes to a carrier.
  const printIt = () => {
    win.focus();
    win.print();
  };
  if (win.document.readyState === "complete") printIt();
  else win.addEventListener("load", printIt, { once: true });
}
